import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Printer, Trash2, X, Settings, Loader2, Pencil, Download } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import {
    silentPrintBarcode,
    silentPrint,
    buildNamedBarcodeLabel,
    browserPrint,
    downloadLabelsPdf,
} from "./helpers/printAgent";
import {
    submitNetworkPrintJob,
    getSavedNetworkPrinter,
} from "./helpers/networkPrint";
import PrinterSelectModal from "./PrinterSelectModal";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";

// One of three operator-facing positions a location barcode can resolve to.
// Mirrors the BE enum in scansheetAdmin.controller.js.
const POSITIONS = ["Goods-In", "Envelopes", "Pallets"] as const;
type Position = typeof POSITIONS[number];

// BF sheet variant for a BF desk. Mirrors the BE bfMode enum: "standard" is
// the six-tote Hot/Cold/Red/Measure flow, "big" the three-tote flow with
// the over-length RED rule (PDBIG). Ignored unless bargainfox is on.
const BF_MODES = ["standard", "big"] as const;
type BfLocationMode = typeof BF_MODES[number];

interface Location {
    barcode: string;
    position: Position;
    warehouse: string;
    bargainfox: boolean;
    bfMode?: BfLocationMode;
    // "Box contents" - the scan sheet asks /scan/check for the ASINs inside
    // each box at this desk. Mirrors the BE productData flag.
    productData: boolean;
}

type LocationDraft = {
    warehouse: string;
    barcode: string;
    position: Position;
    bargainfox: boolean;
    bfMode: BfLocationMode;
    productData: boolean;
};

interface AllSettingsRow {
    warehouse: string;
}

// Mirrors the printOneLabel helper in DesksAdmin so the printing UX is
// identical across both admin sections — network printer first, then a
// chain of local fallbacks ending at the browser print dialog.
const printOneLabel = async (barcode: string, name: string): Promise<{ success: boolean; error?: string }> => {
    const html = buildNamedBarcodeLabel(barcode, name);
    const saved = getSavedNetworkPrinter();
    if (saved) {
        const result = await submitNetworkPrintJob(saved.agentId, saved.printerName, html, 1, barcode);
        if (result.success) return { success: true };
    }
    const zebra = await silentPrintBarcode(barcode);
    if (zebra.success) return { success: true };
    const local = await silentPrint(html);
    if (local.success) return { success: true };
    return browserPrint(html);
};

const ScansheetLocationsAdmin = () => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [warehouses, setWarehouses] = useState<string[]>([]);
    const [locations, setLocations] = useState<Location[]>([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState<LocationDraft | null>(null);
    const [editing, setEditing] = useState<(LocationDraft & { originalBarcode: string }) | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [showPrinterSelect, setShowPrinterSelect] = useState(false);
    const [removeConfirm, setRemoveConfirm] = useState<Location | null>(null);
    const [removing, setRemoving] = useState(false);
    const [printingBarcode, setPrintingBarcode] = useState<string | null>(null);
    const [printingAllWh, setPrintingAllWh] = useState<string | null>(null);
    const [downloadingWh, setDownloadingWh] = useState<string | null>(null);
    const [printerNeedsSetup, setPrinterNeedsSetup] = useState(false);
    const [togglingBfBarcode, setTogglingBfBarcode] = useState<string | null>(null);

    // Settings call to enumerate warehouses, locations call for the live
    // list (which already filters out soft-deleted entries server-side).
    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [whRes, locRes] = await Promise.all([
                apiFetch(`/settings`, {}),
                apiFetch(`/scansheet-admin/locations/list`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: "{}",
                }),
            ]);
            if (!whRes.ok || !locRes.ok) throw new Error("load");
            const whRows: AllSettingsRow[] = await whRes.json();
            const locData = await locRes.json();
            setWarehouses(whRows.map(r => r.warehouse).sort());
            setLocations(Array.isArray(locData.locations) ? locData.locations : []);
        } catch {
            toast.error(t("server_error"), { toastId: "scansheet-loc-load-err" });
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { refresh(); }, [refresh]);

    // Group by warehouse for the per-warehouse <details> rendering.
    const locationsByWarehouse = useMemo(() => {
        const map: Record<string, Location[]> = {};
        for (const wh of warehouses) map[wh] = [];
        for (const loc of locations) {
            if (!map[loc.warehouse]) map[loc.warehouse] = [];
            map[loc.warehouse].push(loc);
        }
        for (const wh of Object.keys(map)) {
            map[wh].sort((a, b) => a.barcode.localeCompare(b.barcode));
        }
        return map;
    }, [warehouses, locations]);

    const positionLabel = useCallback((p: Position) => {
        if (p === "Goods-In") return t("position_goods_in");
        if (p === "Envelopes") return t("position_envelopes");
        return t("position_pallets");
    }, [t]);

    const modeLabel = useCallback((m: BfLocationMode) => t(`bf_location_mode_${m}`), [t]);
    const modeOf = (loc: Pick<Location, "bfMode">): BfLocationMode => loc.bfMode === "big" ? "big" : "standard";

    // Print label shows position + warehouse beneath the barcode so an
    // operator scanning the same code at different sites still gets a
    // unique-looking sticker.
    const labelDisplayName = (loc: Pick<Location, "position" | "warehouse">) =>
        `${loc.position} - ${loc.warehouse}`;

    const handleAdd = async () => {
        if (!adding) return;
        const code = adding.barcode.trim().toUpperCase();
        if (!code) return;
        setSubmitting(true);
        try {
            const res = await apiFetch(`/scansheet-admin/locations/add`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    barcode: code,
                    position: adding.position,
                    warehouse: adding.warehouse,
                    bargainfox: adding.bargainfox,
                    bfMode: adding.bfMode,
                    productData: adding.productData,
                }),
            });
            if (res.status === 409) {
                toast.error(t("location_barcode_taken"), { toastId: "scansheet-loc-dup" });
                return;
            }
            if (!res.ok) throw new Error("add");
            await refresh();
            setAdding(null);
        } catch {
            toast.error(t("server_error"), { toastId: "scansheet-loc-add-err" });
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditSave = async () => {
        if (!editing) return;
        const code = editing.barcode.trim().toUpperCase();
        if (!code) return;
        setSubmitting(true);
        try {
            const res = await apiFetch(`/scansheet-admin/locations/update`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    barcode: editing.originalBarcode,
                    newBarcode: code,
                    position: editing.position,
                    warehouse: editing.warehouse,
                    bargainfox: editing.bargainfox,
                    bfMode: editing.bfMode,
                    productData: editing.productData,
                }),
            });
            if (res.status === 409) {
                toast.error(t("location_barcode_taken"), { toastId: "scansheet-loc-dup" });
                return;
            }
            if (!res.ok) throw new Error("update");
            await refresh();
            setEditing(null);
        } catch {
            toast.error(t("server_error"), { toastId: "scansheet-loc-update-err" });
        } finally {
            setSubmitting(false);
        }
    };

    const confirmRemove = async () => {
        if (!removeConfirm) return;
        setRemoving(true);
        try {
            const res = await apiFetch(`/scansheet-admin/locations/remove`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ barcode: removeConfirm.barcode }),
            });
            if (!res.ok) throw new Error("remove");
            await refresh();
            setRemoveConfirm(null);
        } catch {
            toast.error(t("server_error"), { toastId: "scansheet-loc-remove-err" });
        } finally {
            setRemoving(false);
        }
    };

    // In-list flag toggles (BF / Box info). The two are mutually exclusive
    // (BE rejects both set), so enabling one clears the other in the same
    // request. Other fields are re-sent unchanged.
    const handleFlagToggle = async (loc: Location, flag: "bargainfox" | "productData", on: boolean) => {
        const bargainfox = flag === "bargainfox" ? on : (on ? false : loc.bargainfox === true);
        const patch: Pick<Location, "bargainfox" | "bfMode" | "productData"> = {
            bargainfox,
            // The BE resets the mode when BF is switched off; mirror it locally.
            bfMode: bargainfox ? modeOf(loc) : "standard",
            productData: flag === "productData" ? on : (on ? false : loc.productData === true),
        };
        setTogglingBfBarcode(loc.barcode);
        try {
            const res = await apiFetch(`/scansheet-admin/locations/update`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    barcode: loc.barcode,
                    newBarcode: loc.barcode,
                    position: loc.position,
                    warehouse: loc.warehouse,
                    ...patch,
                }),
            });
            if (!res.ok) throw new Error("toggle");
            setLocations(current => current.map(row => row.barcode === loc.barcode ? { ...row, ...patch } : row));
        } catch {
            toast.error(t("server_error"), { toastId: "scansheet-loc-bf-err" });
        } finally {
            setTogglingBfBarcode(null);
        }
    };

    // In-list BF mode select (only rendered for BF desks).
    const handleModeChange = async (loc: Location, bfMode: BfLocationMode) => {
        setTogglingBfBarcode(loc.barcode);
        try {
            const res = await apiFetch(`/scansheet-admin/locations/update`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    barcode: loc.barcode,
                    newBarcode: loc.barcode,
                    position: loc.position,
                    warehouse: loc.warehouse,
                    bargainfox: true,
                    bfMode,
                    productData: false,
                }),
            });
            if (!res.ok) throw new Error("mode");
            setLocations(current => current.map(row => row.barcode === loc.barcode ? { ...row, bfMode } : row));
        } catch {
            toast.error(t("server_error"), { toastId: "scansheet-loc-mode-err" });
        } finally {
            setTogglingBfBarcode(null);
        }
    };

    const handlePrint = async (loc: Location) => {
        setPrintingBarcode(loc.barcode);
        try {
            const result = await printOneLabel(loc.barcode, labelDisplayName(loc));
            if (!result.success) {
                setPrinterNeedsSetup(true);
                toast.error(t("print_failed"), { toastId: "scansheet-loc-print-err" });
            }
        } finally {
            setPrintingBarcode(null);
        }
    };

    const handlePrintAll = async (warehouse: string) => {
        const list = locationsByWarehouse[warehouse] ?? [];
        if (list.length === 0) return;
        setPrintingAllWh(warehouse);
        try {
            let failures = 0;
            for (const loc of list) {
                const r = await printOneLabel(loc.barcode, labelDisplayName(loc));
                if (!r.success) failures += 1;
            }
            if (failures > 0) {
                setPrinterNeedsSetup(true);
                toast.warn(t("print_partial_failed", { failed: String(failures), total: String(list.length) }));
            }
        } finally {
            setPrintingAllWh(null);
        }
    };

    const handleDownloadAll = async (warehouse: string) => {
        const list = locationsByWarehouse[warehouse] ?? [];
        if (list.length === 0) return;
        setDownloadingWh(warehouse);
        try {
            const result = await downloadLabelsPdf(
                list.map(loc => ({ barcode: loc.barcode, name: labelDisplayName(loc) })),
                `scansheet-locations-${warehouse}.pdf`,
            );
            if (!result.success) {
                toast.error(result.error || t("download_failed"), { toastId: "scansheet-loc-pdf-err" });
            }
        } finally {
            setDownloadingWh(null);
        }
    };

    if (loading) return <div className="admin-settings-loading">{t("loading")}</div>;

    return (
        <div>
            <div className="dispatch-admin-toolbar">
                <button
                    type="button"
                    className={`btn btn-ghost btn-sm ${printerNeedsSetup ? "dispatch-admin-printer-needed" : ""}`}
                    onClick={() => { setShowPrinterSelect(true); setPrinterNeedsSetup(false); }}
                >
                    <Settings size={14} />
                    {t("printer_select")}
                </button>
            </div>

            {warehouses.length === 0 && (
                <div className="admin-settings-empty">{t("no_warehouses")}</div>
            )}

            {warehouses.map(wh => {
                const list = locationsByWarehouse[wh] ?? [];
                return (
                    <details key={wh} className="admin-settings-po-warehouse">
                        <summary className="admin-settings-po-warehouse-summary">
                            <span className="admin-settings-po-warehouse-name">{wh}</span>
                            <span className="admin-settings-po-warehouse-count">
                                {list.length}
                            </span>
                        </summary>
                        <div className="admin-settings-po-editor">
                            <div className="dispatch-admin-rows">
                                {list.map(loc => (
                                    <div key={loc.barcode} className="dispatch-admin-row dispatch-admin-row--desk dispatch-admin-row--scansheet-location">
                                        <span className="dispatch-admin-row-name">{positionLabel(loc.position)}</span>
                                        <span className="dispatch-admin-row-barcode">{loc.barcode}</span>
                                        <div className="scansheet-location-flags">
                                            <label className="admin-settings-toggle scansheet-location-bf-list-toggle" title={t("bf_location_toggle_hint")}>
                                                <span>{t("bf_location_toggle")}</span>
                                                <input
                                                    type="checkbox"
                                                    checked={loc.bargainfox === true}
                                                    disabled={togglingBfBarcode === loc.barcode || readOnly}
                                                    onChange={e => handleFlagToggle(loc, "bargainfox", e.target.checked)}
                                                />
                                                <span className="admin-settings-slider" />
                                            </label>
                                            {loc.bargainfox === true && (
                                                <select
                                                    className="dispatch-admin-form-input scansheet-location-mode"
                                                    value={modeOf(loc)}
                                                    disabled={togglingBfBarcode === loc.barcode}
                                                    onChange={e => handleModeChange(loc, e.target.value as BfLocationMode)}
                                                    title={t("bf_location_mode_hint")}
                                                    aria-label={t("bf_location_mode")}
                                                >
                                                    {BF_MODES.map(m => (
                                                        <option key={m} value={m}>{modeLabel(m)}</option>
                                                    ))}
                                                </select>
                                            )}
                                            <label className="admin-settings-toggle scansheet-location-bf-list-toggle" title={t("box_info_toggle_hint")}>
                                                <span>{t("box_info_toggle")}</span>
                                                <input
                                                    type="checkbox"
                                                    checked={loc.productData === true}
                                                    disabled={togglingBfBarcode === loc.barcode || readOnly}
                                                    onChange={e => handleFlagToggle(loc, "productData", e.target.checked)}
                                                />
                                                <span className="admin-settings-slider" />
                                            </label>
                                        </div>
                                        <div className="dispatch-admin-row-actions-inline">
                                            <button
                                                type="button"
                                                className="dispatch-admin-row-icon"
                                                onClick={() => setEditing({
                                                    originalBarcode: loc.barcode,
                                                    barcode: loc.barcode,
                                                    position: loc.position,
                                                    warehouse: loc.warehouse,
                                                    bargainfox: loc.bargainfox === true,
                                                    bfMode: modeOf(loc),
                                                    productData: loc.productData === true,
                                                })}
                                                disabled={readOnly}
                                                aria-disabled={readOnly}
                                                title={readOnly ? t("read_only_mode") : t("edit")}
                                            >
                                                <Pencil size={16} />
                                            </button>
                                            <button
                                                type="button"
                                                className="dispatch-admin-row-icon"
                                                onClick={() => handlePrint(loc)}
                                                title={t("print_label")}
                                                disabled={printingBarcode === loc.barcode || printingAllWh === wh}
                                            >
                                                {printingBarcode === loc.barcode || printingAllWh === wh
                                                    ? <Loader2 size={16} className="dispatch-admin-spin" />
                                                    : <Printer size={16} />}
                                            </button>
                                            <button
                                                type="button"
                                                className="dispatch-admin-row-icon dispatch-admin-row-icon--danger"
                                                onClick={() => setRemoveConfirm(loc)}
                                                disabled={readOnly}
                                                aria-disabled={readOnly}
                                                title={readOnly ? t("read_only_mode") : t("remove_location")}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                {list.length === 0 && (
                                    <div className="admin-settings-empty">{t("no_locations_yet")}</div>
                                )}
                            </div>
                            <div className="dispatch-admin-row-actions">
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => setAdding({ warehouse: wh, barcode: "", position: "Goods-In", bargainfox: false, bfMode: "standard", productData: false })}
                                    disabled={readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    <Plus size={14} />
                                    {t("add_location")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => handlePrintAll(wh)}
                                    disabled={list.length === 0 || printingAllWh === wh}
                                >
                                    {printingAllWh === wh
                                        ? <Loader2 size={14} className="dispatch-admin-spin" />
                                        : <Printer size={14} />}
                                    {t("print_all")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => handleDownloadAll(wh)}
                                    disabled={list.length === 0 || downloadingWh === wh}
                                >
                                    {downloadingWh === wh
                                        ? <Loader2 size={14} className="dispatch-admin-spin" />
                                        : <Download size={14} />}
                                    {t("download_all")}
                                </button>
                            </div>
                        </div>
                    </details>
                );
            })}

            {adding && (
                <div className="modal-overlay" onClick={() => !submitting && setAdding(null)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 360 }}>
                        <div className="modal-card-header">
                            <h2>{t("add_location")} — {adding.warehouse}</h2>
                            <button className="modal-close" onClick={() => setAdding(null)} disabled={submitting}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <label className="dispatch-admin-form-label">
                                {t("location_barcode")}
                                <input
                                    type="text"
                                    autoFocus
                                    className="dispatch-admin-form-input"
                                    value={adding.barcode}
                                    onChange={e => setAdding({ ...adding, barcode: e.target.value.toUpperCase() })}
                                    onKeyDown={e => { if (e.key === "Enter") handleAdd(); }}
                                    placeholder="NPGI"
                                    maxLength={64}
                                />
                            </label>
                            <label className="dispatch-admin-form-label" style={{ marginTop: 12 }}>
                                {t("location_position")}
                                <select
                                    className="dispatch-admin-form-input"
                                    value={adding.position}
                                    onChange={e => setAdding({ ...adding, position: e.target.value as Position })}
                                >
                                    {POSITIONS.map(p => (
                                        <option key={p} value={p}>{positionLabel(p)}</option>
                                    ))}
                                </select>
                            </label>
                            <div className="scansheet-location-flags scansheet-location-flags--form">
                                <label className="admin-settings-toggle scansheet-location-bf-toggle" title={t("bf_location_toggle_hint")}>
                                    <span>{t("bf_location_toggle")}</span>
                                    <input
                                        type="checkbox"
                                        checked={adding.bargainfox}
                                        onChange={e => setAdding({ ...adding, bargainfox: e.target.checked, productData: e.target.checked ? false : adding.productData })}
                                    />
                                    <span className="admin-settings-slider" />
                                </label>
                                <label className="admin-settings-toggle scansheet-location-bf-toggle" title={t("box_info_toggle_hint")}>
                                    <span>{t("box_info_toggle")}</span>
                                    <input
                                        type="checkbox"
                                        checked={adding.productData}
                                        onChange={e => setAdding({ ...adding, productData: e.target.checked, bargainfox: e.target.checked ? false : adding.bargainfox })}
                                    />
                                    <span className="admin-settings-slider" />
                                </label>
                            </div>
                            {adding.bargainfox && (
                                <label className="dispatch-admin-form-label" style={{ marginTop: 12 }} title={t("bf_location_mode_hint")}>
                                    {t("bf_location_mode")}
                                    <select
                                        className="dispatch-admin-form-input"
                                        value={adding.bfMode}
                                        onChange={e => setAdding({ ...adding, bfMode: e.target.value as BfLocationMode })}
                                    >
                                        {BF_MODES.map(m => (
                                            <option key={m} value={m}>{modeLabel(m)}</option>
                                        ))}
                                    </select>
                                </label>
                            )}
                            <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                                <button className="btn btn-ghost" onClick={() => setAdding(null)} disabled={submitting}>
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-success"
                                    onClick={handleAdd}
                                    disabled={submitting || !adding.barcode.trim() || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {submitting ? t("submitting") : t("add_location")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {editing && (
                <div className="modal-overlay" onClick={() => !submitting && setEditing(null)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
                        <div className="modal-card-header">
                            <h2>{t("edit_location")} — {editing.originalBarcode}</h2>
                            <button className="modal-close" onClick={() => setEditing(null)} disabled={submitting}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <label className="dispatch-admin-form-label">
                                {t("location_barcode")}
                                <input
                                    type="text"
                                    className="dispatch-admin-form-input"
                                    value={editing.barcode}
                                    onChange={e => setEditing({ ...editing, barcode: e.target.value.toUpperCase() })}
                                    maxLength={64}
                                />
                            </label>
                            <label className="dispatch-admin-form-label" style={{ marginTop: 12 }}>
                                {t("location_position")}
                                <select
                                    className="dispatch-admin-form-input"
                                    value={editing.position}
                                    onChange={e => setEditing({ ...editing, position: e.target.value as Position })}
                                >
                                    {POSITIONS.map(p => (
                                        <option key={p} value={p}>{positionLabel(p)}</option>
                                    ))}
                                </select>
                            </label>
                            <div className="scansheet-location-flags scansheet-location-flags--form">
                                <label className="admin-settings-toggle scansheet-location-bf-toggle" title={t("bf_location_toggle_hint")}>
                                    <span>{t("bf_location_toggle")}</span>
                                    <input
                                        type="checkbox"
                                        checked={editing.bargainfox}
                                        onChange={e => setEditing({ ...editing, bargainfox: e.target.checked, productData: e.target.checked ? false : editing.productData })}
                                    />
                                    <span className="admin-settings-slider" />
                                </label>
                                <label className="admin-settings-toggle scansheet-location-bf-toggle" title={t("box_info_toggle_hint")}>
                                    <span>{t("box_info_toggle")}</span>
                                    <input
                                        type="checkbox"
                                        checked={editing.productData}
                                        onChange={e => setEditing({ ...editing, productData: e.target.checked, bargainfox: e.target.checked ? false : editing.bargainfox })}
                                    />
                                    <span className="admin-settings-slider" />
                                </label>
                            </div>
                            {editing.bargainfox && (
                                <label className="dispatch-admin-form-label" style={{ marginTop: 12 }} title={t("bf_location_mode_hint")}>
                                    {t("bf_location_mode")}
                                    <select
                                        className="dispatch-admin-form-input"
                                        value={editing.bfMode}
                                        onChange={e => setEditing({ ...editing, bfMode: e.target.value as BfLocationMode })}
                                    >
                                        {BF_MODES.map(m => (
                                            <option key={m} value={m}>{modeLabel(m)}</option>
                                        ))}
                                    </select>
                                </label>
                            )}
                            <label className="dispatch-admin-form-label" style={{ marginTop: 12 }}>
                                {t("location_warehouse")}
                                <select
                                    className="dispatch-admin-form-input"
                                    value={editing.warehouse}
                                    onChange={e => setEditing({ ...editing, warehouse: e.target.value })}
                                >
                                    {warehouses.map(wh => (
                                        <option key={wh} value={wh}>{wh}</option>
                                    ))}
                                </select>
                            </label>
                            <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                                <button className="btn btn-ghost" onClick={() => setEditing(null)} disabled={submitting}>
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-success"
                                    onClick={handleEditSave}
                                    disabled={submitting || !editing.barcode.trim() || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {submitting ? t("submitting") : t("save")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {removeConfirm && (
                <div className="modal-overlay" onClick={() => !removing && setRemoveConfirm(null)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
                        <div className="modal-card-header">
                            <h2>{t("remove_location")}</h2>
                            <button className="modal-close" onClick={() => setRemoveConfirm(null)} disabled={removing}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p style={{ marginBottom: 16, lineHeight: 1.5 }}>
                                {t("confirm_remove_location", { barcode: removeConfirm.barcode })}
                            </p>
                            <div className="modal-confirm-buttons">
                                <button
                                    className="btn btn-ghost"
                                    onClick={() => setRemoveConfirm(null)}
                                    disabled={removing}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-danger"
                                    onClick={confirmRemove}
                                    disabled={removing || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {removing ? t("submitting") : t("remove_location")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showPrinterSelect && (
                <PrinterSelectModal
                    autoPrint={true}
                    printMode={null}
                    onSelect={() => setShowPrinterSelect(false)}
                    onConnectLocal={() => setShowPrinterSelect(false)}
                    onDisconnect={() => setShowPrinterSelect(false)}
                    onClose={() => setShowPrinterSelect(false)}
                />
            )}
        </div>
    );
};

export default ScansheetLocationsAdmin;
